// IE Hub - Budget Field Mapping Utilities (UPDATED FOR NESTED STRUCTURE)
// Provides field mapping functions between API responses and schema fields

import type {
  CustomerDebtRecord,
  DebtCategoryKey,
  DebtFormEntry,
  DebtFrequencyApi,
  DebtRepaymentFrequencyUi,
  DebtsSchemaState,
  DebtUpsertRequest,
  DebtSource,
  ExpenseDataRecord,
  GuidanceNoteRecord,
  IncomeDataRecord,
  IncomeTriggerRecord,
} from '../api/types';
import { BUDGET_FIELD_MAPPINGS } from './budgetBreakDownFields';

/**
 * Frequency mapping between UI and API formats
 * UI shows full text (weekly, monthly, etc.), API expects codes (W, M, etc.)
 */
const FREQUENCY_UI_TO_API: Record<string, string> = {
  weekly: 'W',
  '2weekly': '2W',
  '4weekly': '4W',
  monthly: 'M',
  quarterly: 'Q',
  semiAnnual: 'SA',
  semiannual: 'SA',
  yearly: 'Y',
};

const FREQUENCY_API_TO_UI: Record<string, string> = {
  W: 'weekly',
  '2W': '2weekly',
  '4W': '4weekly',
  M: 'monthly',
  Q: 'quarterly',
  SA: 'semiannual',
  Y: 'yearly',
};

const DEFAULT_ARREARS_CODES = new Set<string>([
  'GA', // Gas arrears
  'EE', // Electricity arrears
  'WS', // Water supply arrears
  'WA', // Water waste arrears
  'HR', // Housing rent arrears
  'MG', // Mortgage arrears
  'SE', // Secured loan arrears
  'CT', // Council tax arrears
]);

function getCaseInsensitiveValue(
  source: Record<string, unknown>,
  targetKey: string
): unknown {
  const target = targetKey.toLowerCase();
  const matchingKey = Object.keys(source).find(
    (key) => key.toLowerCase() === target
  );
  return matchingKey !== undefined ? source[matchingKey] : undefined;
}

function buildKeyVariants(key: string): string[] {
  if (!key) {
    return [];
  }

  const uppercaseIndices: number[] = [];
  for (let i = 0; i < key.length; i += 1) {
    const charCode = key.charCodeAt(i);
    if (charCode >= 65 && charCode <= 90) {
      uppercaseIndices.push(i);
    }
  }

  const variants = new Set<string>([key]);
  const totalVariants = 1 << uppercaseIndices.length;

  for (let mask = 1; mask < totalVariants; mask += 1) {
    const chars = key.split('');
    uppercaseIndices.forEach((index, bitPosition) => {
      if ((mask >> bitPosition) & 1) {
        const existingChar = chars[index];
        if (existingChar) {
          chars[index] = existingChar.toLowerCase();
        }
      }
    });
    variants.add(chars.join(''));
  }

  return Array.from(variants);
}

function assignNoteValueToTarget(
  target: Record<string, unknown>,
  noteKeys: string[],
  rawValue: unknown
): boolean {
  if (rawValue === undefined || rawValue === null) {
    return false;
  }

  const value = String(rawValue);
  if (value === '') {
    return false;
  }

  const variants = new Set<string>();
  noteKeys.forEach((key) => {
    buildKeyVariants(key).forEach((variant) => variants.add(variant));
  });

  variants.forEach((variant) => {
    target[variant] = value;
  });

  if (!variants.has('notes')) {
    target.notes = value;
  }

  return true;
}

const SECTION_NAME_TO_CATEGORY: Record<string, DebtCategoryKey> = {
  'credit cards': 'creditCards',
  'credit card': 'creditCards',
  creditcards: 'creditCards',
  loans: 'loans',
  loan: 'loans',
  overdraft: 'overdrafts',
  overdrafts: 'overdrafts',
  others: 'otherDebts',
  arrears: 'arrears',
};

const DEFAULT_CATEGORY_BY_CODE: Partial<Record<string, DebtCategoryKey>> = {
  ZC: 'creditCards',
  CC: 'creditCards',
  OD: 'overdrafts',
  LN: 'loans',
  LA: 'loans',
};

type BudgetCategoryKey = keyof typeof BUDGET_FIELD_MAPPINGS;

/**
 * Mapping between section-level yes/no UI toggles in the budget schemas and the
 * underlying backend Y/N flags on the Income API.
 *
 * Example:
 *   - schema toggle `haveBenefits` ↔ API field `haveBenefits`
 *   - schema toggle `haveOtherSources` ↔ API field `haveOtherIncome`
 *   - schema toggle `haveSavings` ↔ API field `isMonthlyContributionToSavings`
 */
const INCOME_TOGGLE_TO_API_FIELD: Partial<
  Record<BudgetCategoryKey, Record<string, string>>
> = {
  benefits: {
    haveBenefits: 'haveBenefits',
  },
  pension: {
    havePension: 'havePension',
  },
  otherSources: {
    // UI schema calls this "other sources"; backend keeps legacy name "haveOtherIncome"
    haveOtherSources: 'haveOtherIncome',
  },
  properties: {
    haveProperties: 'haveProperties',
  },
  savings: {
    // "Are you putting money into savings?" → isMonthlyContributionToSavings (Y/N)
    haveSavings: 'isMonthlyContributionToSavings',
  },
};

/**
 * Mapping between section-level yes/no UI toggles and backend Y/N flags on the
 * Expenses API.
 *
 * These flags are used by the Dashboard and legacy Vue app and must remain
 * in sync with the new JSON-driven Update Budget screens.
 */
const EXPENSE_TOGGLE_TO_API_FIELD: Partial<
  Record<BudgetCategoryKey, Record<string, string>>
> = {
  // Fixed expenses
  household: {
    haveHouseholdExpenses: 'haveHomeAndContents',
  },
  utilities: {
    haveUtilities: 'haveUtilityCosts',
  },
  care: {
    haveCareExpenses: 'haveCareAndHealthCosts',
  },
  transport: {
    haveTransportExpenses: 'haveTransportAndTravelExpenses',
  },
  school: {
    haveSchoolExpenses: 'haveSchoolExpenses',
  },
  pensionInsurance: {
    havePensionInsurance: 'havePensionAndInsuranceExpenses',
  },
  professionals: {
    haveProfessionalCosts: 'haveProfessionalExpenses',
  },
  otherSourcesFixed: {
    haveOtherFixedExpenses: 'haveOtherExpenses',
  },

  // Flexible expenses
  leisure: {
    haveLeisure: 'haveCommsAndLeisureExpenses',
  },
  housekeeping: {
    haveHousekeeping: 'haveFoodAndHousekeepingExpenses',
  },
  personalCosts: {
    havePersonalCosts: 'havePersonalCosts',
  },
  // Debts: debtsAdministration handled separately via hasDebtAdminFee
};

export interface DebtMappingOptions {
  arrearsCodes?: Set<string>;
  accountTypeCategoryMap?: Record<string, DebtCategoryKey>;
  includeRaw?: boolean;
  defaultCategory?: DebtCategoryKey;
  allDebtsAdded?: boolean;
  // Optional helpers for better category resolution when API omits accTypeCode
  accountTypeIdToCodeMap?: Record<number, string>;
  accountTypeLabelToCodeMap?: Record<string, string>;
}

export interface DebtSchemaToApiOptions extends DebtMappingOptions {
  customerId: number;
  defaultSource?: DebtSource;
}

export const mapDebtFrequencyUiToApi = (
  uiFrequency?: string | null
): DebtFrequencyApi | null => {
  if (!uiFrequency) {
    return null;
  }
  const normalized = uiFrequency.trim().toLowerCase();
  return (FREQUENCY_UI_TO_API[normalized] as DebtFrequencyApi | undefined) ?? (normalized as DebtFrequencyApi);
};

export const mapDebtFrequencyApiToUi = (
  apiFrequency?: string | null
): DebtRepaymentFrequencyUi | null => {
  if (!apiFrequency) {
    return null;
  }
  const normalized = apiFrequency.trim().toUpperCase();
  return (FREQUENCY_API_TO_UI[normalized] as DebtRepaymentFrequencyUi | undefined) ?? normalized.toLowerCase();
};

/**
 * Convert a periodic amount to its monthly equivalent
 */
const toMonthlyFromUi = (
  amount: number | null | undefined,
  uiFrequency: DebtRepaymentFrequencyUi | null | undefined
): number | null => {
  if (amount === null || amount === undefined) return null;
  if (!Number.isFinite(amount)) return null;
  switch (uiFrequency) {
    case 'weekly':
      return ((amount / 7) * 365) / 12;
    case '2weekly':
      return ((amount / 14) * 365) / 12;
    case '4weekly':
      return amount * (13 / 12);
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'semiAnnual':
    case 'semiannual':
      return amount / 6;
    case 'yearly':
      return amount / 12;
    default:
      return null;
  }
};

/**
 * Convert a monthly amount to the specified UI frequency periodic amount
 */
const fromMonthlyToUi = (
  monthlyAmount: number | null | undefined,
  uiFrequency: DebtRepaymentFrequencyUi | null | undefined
): number | null => {
  if (monthlyAmount === null || monthlyAmount === undefined) return null;
  if (!Number.isFinite(monthlyAmount)) return null;
  // Compute the periodic amount from the canonical monthly value
  let periodic: number;
  switch (uiFrequency) {
    case 'weekly':
      periodic = (monthlyAmount * 7 * 12) / 365;
      break;
    case '2weekly':
      periodic = (monthlyAmount * 14 * 12) / 365;
      break;
    case '4weekly':
      periodic = monthlyAmount * (12 / 13);
      break;
    case 'monthly':
      periodic = monthlyAmount;
      break;
    case 'quarterly':
      periodic = monthlyAmount * 3;
      break;
    case 'semiAnnual':
    case 'semiannual':
      periodic = monthlyAmount * 6;
      break;
    case 'yearly':
      periodic = monthlyAmount * 12;
      break;
    default:
      periodic = monthlyAmount;
      break;
  }
  // Money-safe rounding for UI display to avoid floating artefacts like 9.9999999998
  return Number(Number.isFinite(periodic) ? periodic.toFixed(2) : null);
};

// Re-export for consumers that import BUDGET_FIELD_MAPPINGS directly
export { BUDGET_FIELD_MAPPINGS };

/**
 * Updated mapping function that handles nested form data structure
 */
export function mapSchemaDataToIncome(
  schemaData: Record<string, any>,
  category: keyof typeof BUDGET_FIELD_MAPPINGS,
  baseRecord: Partial<IncomeDataRecord>
): Partial<IncomeDataRecord> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return baseRecord;

  const result: Partial<IncomeDataRecord> = { ...baseRecord };

   // Map section-level yes/no toggles to backend Y/N fields when configured.
  const incomeToggleMapping = INCOME_TOGGLE_TO_API_FIELD[category as BudgetCategoryKey];
  if (incomeToggleMapping) {
    Object.entries(incomeToggleMapping).forEach(([schemaToggleName, apiFieldName]) => {
      const raw = schemaData[schemaToggleName];
      if (typeof raw === 'boolean') {
        (result as any)[apiFieldName] = raw ? 'Y' : 'N';
      }
    });
  }

  // Iterate through schema data fields
  Object.keys(schemaData).forEach(fieldName => {
    const fieldValue = schemaData[fieldName];
    
    // Skip toggle buttons and non-data fields
    if (typeof fieldValue === 'boolean' || !fieldValue) {
      return;
    }

    // Check if this field has a mapping
    const fieldMapping = (mapping as any)[fieldName];
    if (!fieldMapping || typeof fieldMapping !== 'object') {
      return;
    }

    // Handle nested structure: { amount, frequency, notes }
    if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      const isEmptyObject = Object.keys(fieldValue).length === 0;

      // Map/clear amount
      if (fieldMapping.amount) {
        if (fieldValue.amount !== undefined) {
          const amt = fieldValue.amount;
          const existing = (result as any)[fieldMapping.amount];
          // IMPORTANT:
          // - Empty string means "unanswered" in our JSON-driven UI (unless user explicitly types 0).
          // - We only treat blank as an explicit clear when there was an existing stored value.
          if (amt === '' || amt === null) {
            if (existing !== undefined && existing !== null) {
              (result as any)[fieldMapping.amount] = 0;
            }
          } else {
            const parsed =
              typeof amt === 'number' ? amt : Number.parseFloat(String(amt));
            if (Number.isFinite(parsed)) {
              (result as any)[fieldMapping.amount] = parsed;
            } else if (existing !== undefined && existing !== null) {
              // Fallback clear when an existing value is present but the input is unparsable
              ;(result as any)[fieldMapping.amount] = 0;
            }
          }
        } else if (isEmptyObject) {
          const existing = (result as any)[fieldMapping.amount];
          // Explicit clear only when we are overwriting an existing stored value
          if (existing !== undefined && existing !== null) {
            (result as any)[fieldMapping.amount] = 0;
          }
        }
      }

      // Map/clear frequency (convert UI format to API format)
      if (fieldMapping.frequency) {
        if (fieldValue.frequency !== undefined) {
          const freq = fieldValue.frequency;
          const existing = (result as any)[fieldMapping.frequency];
          if (freq === '' || freq === null) {
            // Only clear an existing stored frequency; don't write null for untouched groups
            if (existing !== undefined && existing !== null && existing !== '') {
              (result as any)[fieldMapping.frequency] = null;
            }
          } else {
            const apiFrequency = FREQUENCY_UI_TO_API[freq] || freq;
            (result as any)[fieldMapping.frequency] = apiFrequency;
          }
        } else if (isEmptyObject) {
          const existing = (result as any)[fieldMapping.frequency];
          // Explicit clear only when we are overwriting an existing stored value
          if (existing !== undefined && existing !== null && existing !== '') {
            (result as any)[fieldMapping.frequency] = null;
          }
        }
      }
    }
  });

  return result;
}

/**
 * Extract notes fields from nested schema data
 */
export function mapSchemaDataToIncomeNotes(
  schemaData: Record<string, any>,
  category: keyof typeof BUDGET_FIELD_MAPPINGS
): Record<string, string> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return {};

  const result: Record<string, string> = {};

  Object.keys(schemaData).forEach(fieldName => {
    const fieldValue = schemaData[fieldName];
    
    // Skip non-object values
    if (typeof fieldValue !== 'object' || Array.isArray(fieldValue) || !fieldValue) {
      return;
    }

    const fieldMapping = (mapping as any)[fieldName];
    if (!fieldMapping || typeof fieldMapping !== 'object') {
      return;
    }

    // Map notes
    if (fieldValue.notes !== undefined && fieldMapping.notes) {
      result[fieldMapping.notes] = fieldValue.notes || '';
    }
  });

  return result;
}

/**
 * Updated mapping function for expenses
 */
export function mapSchemaDataToExpense(
  schemaData: Record<string, any>,
  category: keyof typeof BUDGET_FIELD_MAPPINGS,
  baseRecord: Partial<ExpenseDataRecord>
): Partial<ExpenseDataRecord> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return baseRecord;

  const result: Partial<ExpenseDataRecord> = { ...baseRecord };

  /**
   * Leisure schema special-case:
   *
   * The JSON schema nests `gamblingFromHobbiesLeisure` under:
   *   hobbiesLeisureSports.gamblingFromHobbiesLeisure
   *
   * But the API mapping (`BUDGET_FIELD_MAPPINGS.leisure`) defines
   * `gamblingFromHobbiesLeisure` as a top-level mapping entry.
   *
   * Without hoisting it, saving the leisure section will never persist the
   * gambling answers to the backend, and the follow-up appears to "disappear"
   * when navigating away and back (auto-save / re-hydration).
   */
  const normalizedSchemaData: Record<string, any> = (() => {
    if (category !== 'leisure') {
      return schemaData;
    }
    const nested =
      schemaData?.hobbiesLeisureSports &&
      typeof schemaData.hobbiesLeisureSports === 'object' &&
      !Array.isArray(schemaData.hobbiesLeisureSports)
        ? (schemaData.hobbiesLeisureSports as any).gamblingFromHobbiesLeisure
        : undefined;

    // Prefer an explicit top-level value if it exists (future-proof / legacy support).
    const explicitTopLevel = (schemaData as any).gamblingFromHobbiesLeisure;
    return {
      ...schemaData,
      gamblingFromHobbiesLeisure:
        explicitTopLevel !== undefined ? explicitTopLevel : nested,
    };
  })();

  // We'll iterate over this object for the main mapping pass.
  // For leisure we may remove `gamblingFromHobbiesLeisure` to ensure the
  // safety-net clear wins when hobbies amount is not > 0.
  let schemaDataForIteration: Record<string, any> = normalizedSchemaData;

  // Special-case mapping for debtsAdministration toggle → API Y/N field
  if (category === 'debtsAdministration') {
    const toggle = schemaData['haveDebtAdministration'];
    if (typeof toggle === 'boolean') {
      (result as any).hasDebtAdminFee = toggle ? 'Y' : 'N';
    }
  }

  // Map section-level yes/no toggles for fixed & flexible expenses
  const expenseToggleMapping = EXPENSE_TOGGLE_TO_API_FIELD[category as BudgetCategoryKey];
  if (expenseToggleMapping) {
    Object.entries(expenseToggleMapping).forEach(([schemaToggleName, apiFieldName]) => {
      const raw = schemaData[schemaToggleName];
      if (typeof raw === 'boolean') {
        (result as any)[apiFieldName] = raw ? 'Y' : 'N';
      }
    });
  }

  // Safety net for gambling visibility rule:
  // If hobbiesLeisureSports.amount is not strictly greater than 0,
  // then clear the underlying gamblingFromHobbiesLeisure amount/frequency
  // before mapping to the API. This ensures that backend totals and any
  // subsequent loads of the budget do not resurrect stale gambling values
  // when the parent hobbies amount has been set to 0/empty.
  if (category === 'leisure') {
    const hobbies = normalizedSchemaData.hobbiesLeisureSports;
    const isHobbiesObject =
      hobbies && typeof hobbies === 'object' && !Array.isArray(hobbies);
    const hasHobbiesAmount =
      isHobbiesObject &&
      Object.prototype.hasOwnProperty.call(hobbies as Record<string, unknown>, 'amount');

    // IMPORTANT:
    // Only apply the clearing rule when we actually have a hobbies amount field
    // present in the form snapshot. If the entire hobbies group is missing
    // (e.g. the parent page hasn't captured a snapshot yet), we must NOT wipe
    // gambling values on auto-save.
    if (hasHobbiesAmount) {
      const rawAmt = (hobbies as { amount?: unknown }).amount;
      const amtNum = (() => {
        if (typeof rawAmt === 'number') return rawAmt;
        if (rawAmt === undefined || rawAmt === null) return NaN;
        // Handle formatted strings like "1,000" or "£1000"
        const cleaned = String(rawAmt)
          .replace(/,/g, '')
          .replace(/£/g, '')
          .trim();
        if (cleaned.length === 0) return NaN;
        const parsed = Number.parseFloat(cleaned);
        return Number.isFinite(parsed) ? parsed : NaN;
      })();
      const isPositive = Number.isFinite(amtNum) && amtNum > 0;

      if (!isPositive) {
        // Ensure we don't map any stale gambling values from the form when the
        // parent hobbies amount is 0/empty (the UI hides the follow-up in this case).
        if (
          Object.prototype.hasOwnProperty.call(
            schemaDataForIteration,
            'gamblingFromHobbiesLeisure'
          )
        ) {
          schemaDataForIteration = { ...schemaDataForIteration };
          delete (schemaDataForIteration as any).gamblingFromHobbiesLeisure;
        }

        const gamblingMapping = (mapping as any).gamblingFromHobbiesLeisure;
        if (gamblingMapping && typeof gamblingMapping === 'object') {
          if (gamblingMapping.amount) {
            (result as any)[gamblingMapping.amount] = 0;
          }
          if (gamblingMapping.frequency) {
            (result as any)[gamblingMapping.frequency] = null;
          }
        }
      }
    }
  }

  Object.keys(schemaDataForIteration).forEach(fieldName => {
    const fieldValue = schemaDataForIteration[fieldName];
    
    // Skip toggle buttons and non-data fields
    if (typeof fieldValue === 'boolean' || !fieldValue) {
      return;
    }

    const fieldMapping = (mapping as any)[fieldName];
    if (!fieldMapping || typeof fieldMapping !== 'object') {
      return;
    }

    // Handle nested structure
    if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      const isEmptyObject = Object.keys(fieldValue).length === 0;

      // Map/clear amount
      if (fieldMapping.amount) {
        if (fieldValue.amount !== undefined) {
          const amt = fieldValue.amount;
          const existing = (result as any)[fieldMapping.amount];
          if (amt === '' || amt === null) {
            if (existing !== undefined && existing !== null) {
              (result as any)[fieldMapping.amount] = 0;
            }
          } else {
            const parsed =
              typeof amt === 'number' ? amt : Number.parseFloat(String(amt));
            if (Number.isFinite(parsed)) {
              (result as any)[fieldMapping.amount] = parsed;
            } else if (existing !== undefined && existing !== null) {
              ;(result as any)[fieldMapping.amount] = 0;
            }
          }
        } else if (isEmptyObject) {
          const existing = (result as any)[fieldMapping.amount];
          if (existing !== undefined && existing !== null) {
            (result as any)[fieldMapping.amount] = 0;
          }
        }
      }

      // Map/clear frequency (convert UI format to API format)
      if (fieldMapping.frequency) {
        if (fieldValue.frequency !== undefined) {
          const freq = fieldValue.frequency;
          const existing = (result as any)[fieldMapping.frequency];
          if (freq === '' || freq === null) {
            if (existing !== undefined && existing !== null && existing !== '') {
              (result as any)[fieldMapping.frequency] = null;
            }
          } else {
            const apiFrequency = FREQUENCY_UI_TO_API[freq] || freq;
            (result as any)[fieldMapping.frequency] = apiFrequency;
          }
        } else if (isEmptyObject) {
          const existing = (result as any)[fieldMapping.frequency];
          if (existing !== undefined && existing !== null && existing !== '') {
            (result as any)[fieldMapping.frequency] = null;
          }
        }
      }

      // Map supplier fields (for utilities and leisure)
      const supplierFields = ['landlineSupplier', 'landlineSupplierOther', 'broadbandSupplier',
                              'broadbandSupplierOther', 'mobileSupplier', 'mobileSupplierOther',
                              'gasSupplier', 'gasSupplierOther', 'electricitySupplier', 'electricitySupplierOther',
                              'awarePriorityServices', 'awareCarbonMonoxidePoison'];
      
      supplierFields.forEach(supplierField => {
        if (fieldValue[supplierField] !== undefined && (fieldMapping as any)[supplierField]) {
          let mappedValue = fieldValue[supplierField];
          
          // Convert boolean to Y/N for specific fields
          if ((supplierField === 'awarePriorityServices' || supplierField === 'awareCarbonMonoxidePoison') && typeof mappedValue === 'boolean') {
            mappedValue = mappedValue ? 'Y' : 'N';
          }
          
          (result as any)[(fieldMapping as any)[supplierField]] = mappedValue;
        }
      });

      // Map nested sub-groups inside this field when present in category mapping
      Object.keys(fieldValue).forEach((subFieldName) => {
        const subValue = (fieldValue as any)[subFieldName];
        const subMapping = (mapping as any)[subFieldName];
        if (!subMapping || typeof subMapping !== 'object' || typeof subValue !== 'object' || Array.isArray(subValue) || !subValue) {
          return;
        }
        if (subMapping.amount) {
          const amt = subValue.amount;
          if (amt !== undefined) {
            (result as any)[subMapping.amount] = (amt === '' || amt === null) ? 0 : (parseFloat(amt) || 0);
          }
        }
        if (subMapping.frequency) {
          const freq = subValue.frequency;
          if (freq === '' || freq === null || freq === undefined) {
            (result as any)[subMapping.frequency] = null;
          } else {
            const apiFrequency = FREQUENCY_UI_TO_API[freq] || freq;
            (result as any)[subMapping.frequency] = apiFrequency;
          }
        }
      });
    }
  });

  return result;
}

/**
 * Extract notes fields from expense schema data
 */
export function mapSchemaDataToExpenseNotes(
  schemaData: Record<string, any>,
  category: keyof typeof BUDGET_FIELD_MAPPINGS
): Record<string, string> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return {};

  const result: Record<string, string> = {};

  Object.keys(schemaData).forEach(fieldName => {
    const fieldValue = schemaData[fieldName];
    
    if (typeof fieldValue !== 'object' || Array.isArray(fieldValue) || !fieldValue) {
      return;
    }

    const fieldMapping = (mapping as any)[fieldName];
    if (!fieldMapping || typeof fieldMapping !== 'object') {
      return;
    }

    const noteTargetKey =
      typeof fieldMapping.notes === 'string' ? fieldMapping.notes : undefined;
    if (noteTargetKey) {
      const fieldValueRecord = fieldValue as Record<string, unknown>;
      const rawNote =
        fieldValueRecord.notes ??
        fieldValueRecord[noteTargetKey] ??
        getCaseInsensitiveValue(fieldValueRecord, noteTargetKey);
      if (rawNote !== undefined) {
        result[noteTargetKey] =
          rawNote === null || rawNote === undefined ? '' : String(rawNote);
      }
    }

    // Map nested sub-group notes if present
    Object.keys(fieldValue).forEach((subFieldName) => {
      const subValue = (fieldValue as any)[subFieldName];
      const subMapping = (mapping as any)[subFieldName];
      if (!subMapping || typeof subMapping !== 'object' || typeof subValue !== 'object' || Array.isArray(subValue) || !subValue) {
        return;
      }
      const subNoteTargetKey =
        typeof subMapping.notes === 'string' ? subMapping.notes : undefined;
      if (subNoteTargetKey) {
        const subValueRecord = subValue as Record<string, unknown>;
        const rawSubNote =
          subValueRecord.notes ??
          subValueRecord[subNoteTargetKey] ??
          getCaseInsensitiveValue(subValueRecord, subNoteTargetKey);
        if (rawSubNote !== undefined) {
          result[subNoteTargetKey] =
            rawSubNote === null || rawSubNote === undefined
              ? ''
              : String(rawSubNote);
        }
      }
    });
  });

  return result;
}

/**
 * Mapping of expense field names to arrears accTypeCode
 * Used to extract arrears from expense forms and save them to Debts API
 */
const EXPENSE_FIELD_TO_ARREARS_CODE: Record<string, string> = {
  houseRent: 'HR', // Housing Rent Arrears (defaulting to rent, mortgage would need separate handling)
  mortgage: 'MG', // Mortgage Arrears
  securedLoans: 'SE', // Secured Loan Arrears
  councilTax: 'CT', // Council Tax Arrears
  gas: 'GA', // Gas Arrears
  electricity: 'EE', // Electricity Arrears
  waterSupply: 'WS', // Water Supply Arrears (legacy key)
  water: 'WS', // Water Supply Arrears (schema key)
  waterWaste: 'WA', // Water Waste Arrears
};

/**
 * Extract arrears data from expense form schema and transform to debt payloads
 * Arrears are stored as list-form arrays within expense field groups
 */
export function extractArrearsFromExpenseSchema(
  schemaData: Record<string, any>,
  category: keyof typeof BUDGET_FIELD_MAPPINGS,
  customerId: number
): DebtUpsertRequest[] {
  const result: DebtUpsertRequest[] = [];
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return result;

  // Iterate through expense fields that may contain arrears
  Object.keys(schemaData).forEach(fieldName => {
    const fieldValue = schemaData[fieldName];
    
    // Skip if not an object or is an array (list-form fields are nested within objects)
    if (typeof fieldValue !== 'object' || Array.isArray(fieldValue) || !fieldValue) {
      return;
    }

    // Check if this field has arrears data
    const arrearsArray = fieldValue.arrears;
    if (!Array.isArray(arrearsArray) || arrearsArray.length === 0) {
      return;
    }

    // Get the accTypeCode for this expense field
    const accTypeCode = EXPENSE_FIELD_TO_ARREARS_CODE[fieldName];
    if (!accTypeCode) {
      console.warn(`No arrears code mapping found for expense field: ${fieldName}`);
      return;
    }

    // Transform each arrears entry to a debt payload
    arrearsArray.forEach((arrearsEntry: any) => {
      if (!arrearsEntry || !arrearsEntry.companyName) {
        return; // Skip incomplete entries
      }

      // Extract amount and frequency from arrears.
      // Support both:
      //  - nested shape: { amountCurrentlyOwned: { amount, frequency } }
      //  - flat shape from ArrearForm dialog: { amountCurrentlyOwned: string, repaymentAmount: string, frequency: string }
      //
      // We treat "amount currently owed" as the debt's amountOwed and the explicit
      // repaymentAmount (when present) as the scheduled repayment. This keeps
      // arrears-created debts aligned with the Debts screen mapping.
      let amountOwed: number | undefined;
      let repaymentAmount: number | undefined;
      let repaymentFrequency: DebtFrequencyApi | undefined;
      try {
        if (arrearsEntry.amountCurrentlyOwned && typeof arrearsEntry.amountCurrentlyOwned === 'object') {
          const amountStr = arrearsEntry.amountCurrentlyOwned.amount;
          if (amountStr !== undefined && amountStr !== '' && amountStr !== null) {
            amountOwed = parseFloat(String(amountStr));
            if (!Number.isFinite(amountOwed)) amountOwed = undefined;
          }
          const freqStr = arrearsEntry.amountCurrentlyOwned.frequency;
          if (freqStr) {
            repaymentFrequency = mapDebtFrequencyUiToApi(freqStr) ?? undefined;
          }
        } else {
          // Flat shape fallback
          const flatAmountOwed = arrearsEntry.amountCurrentlyOwned;
          const flatRepayment = arrearsEntry.repaymentAmount;

          if (flatAmountOwed !== undefined && flatAmountOwed !== '' && flatAmountOwed !== null) {
            const n = parseFloat(String(flatAmountOwed).toString().replace(/,/g, ''));
            if (Number.isFinite(n)) amountOwed = n;
          }

          if (flatRepayment !== undefined && flatRepayment !== '' && flatRepayment !== null) {
            const n = parseFloat(String(flatRepayment).toString().replace(/,/g, ''));
            if (Number.isFinite(n)) repaymentAmount = n;
          }

          const flatFreq = arrearsEntry.frequency;
          if (flatFreq) {
            repaymentFrequency = mapDebtFrequencyUiToApi(String(flatFreq)) ?? undefined;
          }
        }
      } catch {
        // ignore parsing errors and fall back to undefined values
      }

      // Determine creditor name (manual or selected).
      // Treat both "__manual_company__" (new-system internal value) and "Other"
      // (legacy/manual selection) as manual entries that should use companyNameManual.
      const isManualCompany =
        arrearsEntry.companyName === '__manual_company__' ||
        arrearsEntry.companyName === 'Other';

      const rawManualName = (arrearsEntry.companyNameManual || '').trim();
      const rawCompanyName = String(arrearsEntry.companyName ?? '').trim();

      const creditorName = isManualCompany
        ? rawManualName
        : rawCompanyName;

      if (!creditorName) {
        return; // Skip if no creditor name
      }

      // Compute repaymentMonthly from the explicit repaymentAmount when present;
      // fall back to amountOwed when no separate repayment has been captured.
      const uiRepaymentFrequency = repaymentFrequency
        ? mapDebtFrequencyApiToUi(repaymentFrequency)
        : null;

      // Only persist repayment when the user has provided an explicit repaymentAmount.
      // Do NOT fall back to amountOwed, otherwise we overwrite repayment with the owed balance.
      const repaymentAmountNumber =
        typeof repaymentAmount === 'number' && Number.isFinite(repaymentAmount)
          ? repaymentAmount
          : null;
      const hasExplicitRepayment = repaymentAmountNumber !== null;
      const repaymentMonthly = hasExplicitRepayment
        ? toMonthlyFromUi(repaymentAmountNumber, uiRepaymentFrequency)
        : null;

      // For existing arrears (has id) with no explicit repayment, omit repayment fields to preserve
      // the backend value. For new arrears without repayment, send nulls explicitly.
      const repaymentFields =
        hasExplicitRepayment
          ? {
              repayment: repaymentAmountNumber,
              repaymentMonthly: repaymentMonthly ?? null,
            }
          : arrearsEntry.id
            ? {}
            : { repayment: null, repaymentMonthly: null };

      // Under `exactOptionalPropertyTypes`, optional properties cannot be explicitly set to `undefined`.
      // We therefore omit keys entirely when we want "no change" semantics (which also mirrors how
      // JSON.stringify drops `undefined` values).
      const accountNumberPayload: string | undefined =
        arrearsEntry.referenceNumber
          ? String(arrearsEntry.referenceNumber)
          : arrearsEntry.noReferenceNumber
            ? ''
            : undefined;

      const payload: DebtUpsertRequest = {
        ...(typeof arrearsEntry.id === 'number' ? { id: arrearsEntry.id } : {}),
        customerId,
        creditor: creditorName,
        accTypeCode,
        ...(accountNumberPayload !== undefined
          ? { accountNumber: accountNumberPayload }
          : {}),
        amountOwed: amountOwed ?? null,
        frequency: repaymentFrequency ?? null,
        ...(arrearsEntry.notes ? { notes: String(arrearsEntry.notes) } : {}),
        source: 'customer_expenses',
        debtType: 'PRIORITY_DEBTS',
        ...repaymentFields,
      };

      result.push(payload);
    });
  });

  return result;
}

const normalizeDebtSource = (source: DebtSource | undefined | null): string =>
  String(source ?? '').toLowerCase();

const isCustomerExpensesSource = (source: DebtSource | undefined | null): boolean =>
  normalizeDebtSource(source) === 'customer_expenses';

const parseDateSafe = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Map arrears from Debts API back to expense form schema structure.
 *
 * Behaviour:
 * - Group debts by `accTypeCode` → expense field using `EXPENSE_FIELD_TO_ARREARS_CODE`.
 * - For each field, select **one** debt to show in the arrears list:
 *   - If multiple debts share the same `accTypeCode`, prefer those with `source = "customer_expenses"`.
 *   - Within that preferred set, pick the earliest `creationDate` when available,
 *     otherwise fall back to the first encountered record.
 * - The selected debt is mapped to the arrears entry format and wrapped in a single‑item array.
 */
export function mapArrearsDebtsToExpenseSchema(
  debts: CustomerDebtRecord[],
  _category: keyof typeof BUDGET_FIELD_MAPPINGS
): Record<string, any> {
  const result: Record<string, any> = {};

  // Create reverse mapping: accTypeCode -> expense field name
  const arrearsCodeToField: Record<string, string> = {};
  Object.entries(EXPENSE_FIELD_TO_ARREARS_CODE).forEach(([fieldName, code]) => {
    arrearsCodeToField[code] = fieldName;
  });

  // Group debts by expense field (keep full debt records so we can apply selection rules)
  const debtsByField: Record<string, CustomerDebtRecord[]> = {};

  debts.forEach((debt) => {
    // Only process debts with a valid accTypeCode (no source restriction)
    const accTypeCode = (debt.accTypeCode ?? '').trim();
    if (!accTypeCode) {
      return;
    }

    const fieldName = arrearsCodeToField[accTypeCode];
    if (!fieldName) {
      return; // Not an expense arrears debt
    }

    if (!debtsByField[fieldName]) {
      debtsByField[fieldName] = [];
    }
    debtsByField[fieldName].push(debt);
  });

  // For each field, pick a single "best" debt and map it to the arrears entry structure
  Object.entries(debtsByField).forEach(([fieldName, fieldDebts]) => {
    if (!fieldDebts || fieldDebts.length === 0) {
      return;
    }

    // Prefer debts with source = customer_expenses when there are multiple matches
    const customerExpensesDebts = fieldDebts.filter((d) =>
      isCustomerExpensesSource(d.source)
    );
    const candidates =
      customerExpensesDebts.length > 0 ? customerExpensesDebts : fieldDebts;

    // If, for any reason, we end up with no candidates, skip this field safely
    if (candidates.length === 0) {
      return;
    }

    // Prefer the earliest creationDate among candidates; fallback to first encountered
    const [firstCandidate, ...restCandidates] = candidates;
    if (!firstCandidate) {
      return;
    }

    let selectedDebt = firstCandidate;
    let selectedTimestamp = parseDateSafe(selectedDebt.creationDate ?? null);

    for (let i = 0; i < restCandidates.length; i += 1) {
      const candidate = restCandidates[i];
      if (!candidate) {
        // With noUncheckedIndexedAccess, guard against undefined index access
        continue;
      }

      const candidateTimestamp = parseDateSafe(candidate.creationDate ?? null);

      if (candidateTimestamp === null) {
        continue;
      }

      if (selectedTimestamp === null || candidateTimestamp < selectedTimestamp) {
        selectedDebt = candidate;
        selectedTimestamp = candidateTimestamp;
      }
    }

    // Transform selected debt record to arrears form entry format
    // We also reconstruct the scheduled repayment amount from the
    // monthly value so the arrears UI can show different "amount owed"
    // vs "repayment" values when editing.
    // NOTE:
    // - Some environments still return the owed balance as `mortgageOwingAmt`
    //   instead of `amountOwed`. We normalise both and treat 0 as a valid value
    //   (rather than falling back to an empty string).
    const rawAmountOwed =
      selectedDebt.amountOwed ??
      (selectedDebt as Record<string, unknown>).mortgageOwingAmt ??
      null;
    const amountOwedNumeric = toNumberOrNull(rawAmountOwed);

    const arrearsEntry: any = {
      id: selectedDebt.id,
      companyName: selectedDebt.creditor || '',
      companyNameManual: '', // Will be set if creditor is not in lender list
      referenceNumber: selectedDebt.accountNumber || '',
      noReferenceNumber:
        !selectedDebt.accountNumber || selectedDebt.accountNumber === '',
      amountCurrentlyOwned: {
        // Preserve "0" correctly when the backend reports 0 or 0.0
        amount:
          amountOwedNumeric !== null && amountOwedNumeric !== undefined
            ? String(amountOwedNumeric)
            : '',
        frequency: selectedDebt.frequency
          ? mapDebtFrequencyApiToUi(selectedDebt.frequency)
          : 'monthly',
      },
      // The API stores the exact periodic amount in `repayment`.
      // Fall back to reconstructing from `repaymentMonthly` if `repayment` is unavailable.
      repaymentAmount: (() => {
        const explicitRepayment = toNumberOrNull(selectedDebt.repayment);
        if (explicitRepayment !== null) {
          return String(explicitRepayment);
        }
        const explicitMonthly = toNumberOrNull(selectedDebt.repaymentMonthly);
        const uiFreq =
          mapDebtFrequencyApiToUi(selectedDebt.frequency ?? null) ?? 'monthly';
        // A monthly value of 0 with no explicit repayment means blank, not £0
        if (explicitMonthly) {
          const periodic = fromMonthlyToUi(explicitMonthly, uiFreq);
          return periodic !== null && periodic !== undefined
            ? String(periodic)
            : '';
        }
        return '';
      })(),
      repaymentFrequency: selectedDebt.frequency
        ? mapDebtFrequencyApiToUi(selectedDebt.frequency)
        : 'monthly',
      notes: selectedDebt.notes || '',
    };

    if (!result[fieldName]) {
      result[fieldName] = {};
    }
    // Always expose a single‑item arrears array per field
    result[fieldName].arrears = [arrearsEntry];
  });

  return result;
}

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveDebtCategory = (
  record: CustomerDebtRecord,
  options: DebtMappingOptions
): DebtCategoryKey => {
  const sectionName = ((record as Record<string, unknown>).sectionName ?? '')
    .toString()
    .toLowerCase();
  if (sectionName && SECTION_NAME_TO_CATEGORY[sectionName]) {
    return SECTION_NAME_TO_CATEGORY[sectionName];
  }

  let code = record.accTypeCode?.trim().toUpperCase() ?? '';
  const arrearsCodes = options.arrearsCodes ?? DEFAULT_ARREARS_CODES;

  if (
    record.source?.toLowerCase() === 'customer_expenses' ||
    (code && arrearsCodes.has(code))
  ) {
    return 'arrears';
  }

  // Fallback classification based on source for debts created on the Debts page.
  // This allows us to persist the user's chosen category even when the backend
  // does not return a reliable accTypeCode / sectionName.
  const sourceNorm = (record.source ?? '').toString().trim().toLowerCase();
  if (sourceNorm === 'loan' || sourceNorm === 'loans') {
    return 'loans';
  }
  if (
    sourceNorm === 'creditcard' ||
    sourceNorm === 'credit_card' ||
    sourceNorm === 'credit-cards' ||
    sourceNorm === 'credit_cards' ||
    sourceNorm === 'credit_cards_loan'
  ) {
    return 'creditCards';
  }
  if (sourceNorm === 'overdraft' || sourceNorm === 'overdrafts') {
    return 'overdrafts';
  }
  if (sourceNorm === 'otherdebts' || sourceNorm === 'other_debts' || sourceNorm === 'other-debts') {
    return 'otherDebts';
  }

  if (options.accountTypeCategoryMap && code && options.accountTypeCategoryMap[code]) {
    return options.accountTypeCategoryMap[code];
  }

  if (code && DEFAULT_CATEGORY_BY_CODE[code]) {
    return DEFAULT_CATEGORY_BY_CODE[code] as DebtCategoryKey;
  }

  // Derive code from accTypeId if available
  const accTypeId = (record as Record<string, unknown>).accTypeId as number | undefined;
  if (!code && typeof accTypeId === 'number' && options.accountTypeIdToCodeMap) {
    const derived = options.accountTypeIdToCodeMap[accTypeId];
    if (derived) {
      const derivedCode = derived.trim().toUpperCase();
      if (DEFAULT_CATEGORY_BY_CODE[derivedCode]) {
        return DEFAULT_CATEGORY_BY_CODE[derivedCode] as DebtCategoryKey;
      }
      if (options.accountTypeCategoryMap && options.accountTypeCategoryMap[derivedCode]) {
        return options.accountTypeCategoryMap[derivedCode];
      }
    }
  }

  // Derive code from accountTypeName/label when provided
  const labelRaw = (record as Record<string, unknown>).accountTypeName as string | undefined;
  if (!code && labelRaw && options.accountTypeLabelToCodeMap) {
    const normalizedLabel = labelRaw.trim().toLowerCase();
    const candidate = options.accountTypeLabelToCodeMap[normalizedLabel];
    if (candidate) {
      const candidateCode = candidate.trim().toUpperCase();
      if (DEFAULT_CATEGORY_BY_CODE[candidateCode]) {
        return DEFAULT_CATEGORY_BY_CODE[candidateCode] as DebtCategoryKey;
      }
      if (options.accountTypeCategoryMap && options.accountTypeCategoryMap[candidateCode]) {
        return options.accountTypeCategoryMap[candidateCode];
      }
    }
    // Heuristic: if label contains 'overdraft', classify as overdrafts
    if (/overdraft/i.test(labelRaw)) {
      return 'overdrafts';
    }
  }

  return options.defaultCategory ?? 'otherDebts';
};

const buildDebtFormEntry = (
  record: CustomerDebtRecord,
  category: DebtCategoryKey,
  options: DebtMappingOptions
): DebtFormEntry => {
  const uiFrequency = mapDebtFrequencyApiToUi(record.frequency ?? null);
  const explicitRepayment = toNumberOrNull(record.repayment);
  // Prefer the exact periodic repayment value if the API returned it.
  // Fall back to reconstructing from monthly if explicit repayment is missing.
  // A monthly value of 0 with no explicit repayment means the customer left it blank, so keep it blank.
  const repaymentMonthlyFallback = toNumberOrNull(record.repaymentMonthly);
  const repaymentValue =
    explicitRepayment !== null
      ? explicitRepayment
      : repaymentMonthlyFallback
        ? fromMonthlyToUi(repaymentMonthlyFallback, uiFrequency)
        : null;
  const paymentOfferAmount = toNumberOrNull(
    (record as Record<string, unknown>).paymentOfferAmount ??
      (record as Record<string, unknown>).pymtOfferAmount ??
      (record as Record<string, unknown>).paymentOfferAmt ??
      null
  );
  // Legacy parity:
  // - Backend `paymentOfferMode` is typically "M" (monthly)
  // - The UI's "Preferred payment day" is stored in `paymentPreferredFrequency`
  const paymentOfferMode =
    (record as Record<string, unknown>).paymentPreferredFrequency ??
    (record as Record<string, unknown>).paymentOfferMode ??
    (record as Record<string, unknown>).pymtOfferMode ??
    null;
  const paymentOfferFrequencyApi =
    (record as Record<string, unknown>).paymentOfferMode ??
    (record as Record<string, unknown>).pymtOfferMode ??
    null;

  const entry: DebtFormEntry = {
    ...(typeof record.id === 'number' || typeof record.id === 'string' ? { id: record.id } : {}),
    category,
    creditorName: record.creditor ?? record.lenderName ?? '',
    parentCompanyName:
      record.parentCompanyName ?? ((record as Record<string, unknown>).parentCompanyName as string | null | undefined) ?? null,
    lenderName: record.lenderName ?? null,
    accTypeCode: record.accTypeCode ?? null,
    accTypeId: (record as Record<string, unknown>).accTypeId as number | undefined ?? null,
    accountTypeName: record.accountTypeName ?? null,
    accountNumber: record.accountNumber ?? null,
    amountOwed: toNumberOrNull(record.amountOwed ?? (record as Record<string, unknown>).balance ?? null),
    repaymentAmount: repaymentValue,
    repaymentFrequency: uiFrequency,
    paymentOfferAmount,
    paymentOfferFrequency: mapDebtFrequencyApiToUi(paymentOfferFrequencyApi as string | null),
    paymentOfferId: (record as Record<string, unknown>).paymentOfferId as number | undefined ?? null,
    paymentOfferMode: paymentOfferMode as DebtFrequencyApi | null,
    shareBudget: record.shareBudget ?? null,
    shareNow: record.shareNow ?? null,
    isDigital: record.isDigital?.toUpperCase() === 'Y',
    schemaName: record.schemaName ?? null,
    subDomain: (record as Record<string, unknown>).subDomain as string | undefined ?? null,
    isPriority: record.debtType?.toUpperCase() === 'PRIORITY_DEBTS',
    ...(record.source ? { source: record.source } : {}),
    debtType: record.debtType ?? null,
    notes: record.notes ?? (record as Record<string, unknown>).notes?.toString() ?? null,
    arrearsCode: category === 'arrears' ? record.accTypeCode ?? null : null,
  };

  // Debug: log when a payment-offer has been mapped from API → form entry
  if (paymentOfferAmount !== null || (record as Record<string, unknown>).paymentOfferId != null) {
    // eslint-disable-next-line no-console
    console.log('[Debts] buildDebtFormEntry mapped payment offer', {
      recordId: record.id,
      category,
      paymentOfferAmount,
      paymentOfferMode,
      rawPaymentOfferId: (record as Record<string, unknown>).paymentOfferId,
    });
  }

  if (options.includeRaw) {
    entry.raw = record;
  }

  return entry;
};

export const EMPTY_DEBTS_SCHEMA_STATE: DebtsSchemaState = {
  haveDebts: false,
  creditCards: [],
  loans: [],
  overdrafts: [],
  otherDebts: [],
  arrears: [],
};

export const mapDebtApiToSchema = (
  debts: CustomerDebtRecord[] | null | undefined,
  options: DebtMappingOptions = {}
): DebtsSchemaState => {
  if (!Array.isArray(debts) || debts.length === 0) {
    return { ...EMPTY_DEBTS_SCHEMA_STATE };
  }

  const state: DebtsSchemaState = {
    haveDebts: false,
    creditCards: [],
    loans: [],
    overdrafts: [],
    otherDebts: [],
    arrears: [],
  };

  debts.forEach((record) => {
    const category = resolveDebtCategory(record, options);
    const entry = buildDebtFormEntry(record, category, options);
    (state[category] as DebtFormEntry[]).push(entry);
  });

  state.haveDebts =
    state.creditCards.length +
      state.loans.length +
      state.overdrafts.length +
      state.otherDebts.length +
      state.arrears.length >
    0;

  if (typeof options.allDebtsAdded === 'boolean') {
    state.allDebtsAdded = options.allDebtsAdded;
  }

  return state;
};

export const mapSchemaDebtsToApi = (
  schema: DebtsSchemaState | null | undefined,
  options: DebtSchemaToApiOptions
): DebtUpsertRequest[] => {
  if (!schema) {
    return [];
  }

  const result: DebtUpsertRequest[] = [];

  const categories: DebtCategoryKey[] = ['creditCards', 'loans', 'overdrafts', 'otherDebts', 'arrears'];

  categories.forEach((category) => {
    const items = schema[category] ?? [];
    items.forEach((entry: DebtFormEntry) => {
      if (!entry || !entry.creditorName) {
        return;
      }

      const frequency = mapDebtFrequencyUiToApi(entry.repaymentFrequency);
      const amountOwed = toNumberOrNull(entry.amountOwed);
      const repayment = toNumberOrNull(entry.repaymentAmount);
      const repaymentMonthly = toMonthlyFromUi(repayment, entry.repaymentFrequency ?? null);

      const source: DebtSource | undefined = entry.source
        ? entry.source
        : category === 'arrears'
          ? 'customer_expenses'
          : category === 'creditCards'
            ? 'creditcard'
            : category === 'loans'
              ? 'loan'
              : category === 'overdrafts'
                ? 'overdraft'
                : category === 'otherDebts'
                  ? 'otherdebts'
                  : options.defaultSource;

      // Set default accTypeCode based on category if not already set
      let accTypeCode = entry.accTypeCode ?? undefined;
      if (!accTypeCode) {
        switch (category) {
          case 'creditCards':
            accTypeCode = 'CC';
            break;
          case 'loans':
            accTypeCode = 'LN';
            break;
          case 'overdrafts':
            accTypeCode = 'OD';
            break;
          // otherDebts and arrears don't have specific codes, leave as undefined
        }
      }

      const payload: DebtUpsertRequest = {
        ...(typeof entry.id === 'number' ? { id: entry.id } : {}),
        customerId: options.customerId,
        creditor: entry.creditorName,
        accTypeCode: accTypeCode ?? null,
        accountNumber: entry.accountNumber ?? null,
        amountOwed,
        repayment,
        frequency: frequency ?? null,
        notes: entry.notes ?? null,
        shareBudget: entry.shareBudget ?? null,
        ...(source ? { source } : {}),
        debtType: entry.debtType ?? (category === 'arrears' ? 'PRIORITY_DEBTS' : 'NON_PRIORITY_DEBTS'),
        repaymentMonthly,
      };

      result.push(payload);
    });
  });

  return result;
};

/**
 * Check if a field value exceeds trigger limits
 * @param fieldValue - The field value to check
 * @param fieldName - The field name to match against triggers
 * @param triggers - Array of trigger records
 * @returns Object with trigger information or null if no trigger found
 */
export function checkFieldTrigger(
  fieldValue: number,
  fieldName: string,
  triggers: IncomeTriggerRecord[]
): {
  exceeded: boolean;
  trigger: IncomeTriggerRecord | null;
  message: string;
} {
  const trigger = triggers.find(t =>
    t.fieldName.toLowerCase() === fieldName.toLowerCase() ||
    t.columnName.toLowerCase() === fieldName.toLowerCase()
  );

  if (!trigger || trigger.upperLimit === undefined) {
    return { exceeded: false, trigger: null, message: '' };
  }

  const exceeded = fieldValue > trigger.upperLimit;

  let message = '';
  if (exceeded) {
    message = `Value exceeds the recommended limit of £${trigger.upperLimit.toLocaleString()}`;
    if (trigger.lowerLimit !== undefined) {
      message += `. Recommended range: £${trigger.lowerLimit.toLocaleString()} - £${trigger.upperLimit.toLocaleString()}`;
    }
  }

  return { exceeded, trigger, message };
}

/**
 * Get guidance notes for a specific API field
 * @param fieldNameInAPI - The API field name to look up
 * @param guidanceNotes - Array of guidance note records
 * @returns The guidance notes text or null if not found
 */
export function getGuidanceNotesForField(
  fieldNameInAPI: string,
  guidanceNotes: GuidanceNoteRecord[]
): string | null {
  const note = guidanceNotes.find(note => note.fieldNameInAPI === fieldNameInAPI);
  return note?.guidanceNotes || null;
}

// Frequency mode constants
export const FREQUENCY_MODES = {
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  FOUR_WEEKLY: '4weekly',
} as const;

/**
 * Default frequency modes for different field types
 */
export const DEFAULT_FREQUENCY_MODES = {
  income: FREQUENCY_MODES.MONTHLY,
  benefits: FREQUENCY_MODES.MONTHLY,
  pensions: FREQUENCY_MODES.MONTHLY,
  assets: null, // Assets don't have frequency modes
  expenses: FREQUENCY_MODES.MONTHLY,
  debts: FREQUENCY_MODES.MONTHLY,
} as const;

/**
 * Transform income data record to schema-compatible nested format
 * API data: { basicIncome: 100, basicIncomeMode: 'monthly', basicIncomeNotes: 'test' }
 * Schema format: { takeHomeSalary: { amount: '100', frequency: 'monthly', notes: 'test' } }
 */
export function mapIncomeDataToSchema(
  incomeData: IncomeDataRecord,
  incomeNotes: Record<string, string>,
  category: keyof typeof BUDGET_FIELD_MAPPINGS
): Record<string, any> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return {};

  const result: Record<string, any> = {};
  let hasAnyData = false;
  // Track whether we saw at least one strictly positive amount across fields.
  // This drives the section-level "haveX" toggles so they reflect real spend,
  // not just default 0 initialisation from the API.
  // Track whether this category actually has a **non‑zero** amount.
  // New customers often have default 0 values from the API; those should NOT
  // force the section toggle (haveBenefits, havePension, etc.) to true.
  let hasAnyPositiveAmount = false;
  // Track when we have an explicit Y/N flag from the backend for this section.
  let hasExplicitToggleFromApi = false;

  // Iterate through schema field mappings
  Object.keys(mapping).forEach(schemaFieldName => {
    const fieldMapping = (mapping as any)[schemaFieldName];
    
    if (typeof fieldMapping !== 'object') {
      return; // Skip non-nested mappings
    }

    // Build nested object from API data
    const nestedObj: any = {};
    let hasData = false;

    if (fieldMapping.amount) {
      const apiValue = (incomeData as any)[fieldMapping.amount];
      // Include 0 values so cleared fields show as £0.00 instead of disappearing
      if (apiValue !== undefined && apiValue !== null) {
        nestedObj.amount = String(apiValue);
        hasData = true;
        const numeric =
          typeof apiValue === 'number'
            ? apiValue
            : Number.parseFloat(String(apiValue));
        if (Number.isFinite(numeric) && numeric > 0) {
          hasAnyPositiveAmount = true;
        }
      }
    }

    if (fieldMapping.frequency) {
      const apiValue = (incomeData as any)[fieldMapping.frequency];
      if (apiValue !== undefined && apiValue !== null) {
        // Convert API format to UI format
        const uiFrequency = FREQUENCY_API_TO_UI[apiValue] || apiValue;
        nestedObj.frequency = uiFrequency;
        hasData = true;
      } else if (fieldMapping.frequency === 'councilTaxReductionMode') {
        // Default Council Tax Support to monthly when the API has no mode yet
        nestedObj.frequency = 'monthly';
      }
    }

    if (fieldMapping.notes) {
      const apiValue = incomeNotes[fieldMapping.notes];
      if (apiValue !== undefined && apiValue !== null) {
        nestedObj.notes = apiValue;
        hasData = true;
      }
    }

    // Only add to result if there's actual data
    if (hasData) {
      result[schemaFieldName] = nestedObj;
      hasAnyData = true;
    }
  });

  // Map explicit backend Y/N flags to section-level toggles when present.
  const incomeToggleMapping = INCOME_TOGGLE_TO_API_FIELD[category as BudgetCategoryKey];
  if (incomeToggleMapping) {
    Object.entries(incomeToggleMapping).forEach(([schemaToggleName, apiFieldName]) => {
      const raw = (incomeData as any)[apiFieldName];
      if (typeof raw === 'string' && raw.trim() !== '') {
        const upper = raw.trim().toUpperCase();
        if (upper === 'Y' || upper === 'N') {
          (result as any)[schemaToggleName] = upper === 'Y';
          hasAnyData = true;
          hasExplicitToggleFromApi = true;
        }
      }
    });
  }

  // Auto‑set toggle fields based on category when there is **real** data.
  // We only consider a category "present" when at least one underlying API
  // amount is strictly greater than zero. This prevents new customers whose
  // records are initialised with 0 values from seeing toggles pre‑selected.
  if (!hasExplicitToggleFromApi && hasAnyData && hasAnyPositiveAmount) {
    if (category === 'benefits') {
      result.haveBenefits = true;
    } else if (category === 'pension') {
      result.havePension = true;
    } else if (category === 'otherSources') {
      result.haveOtherSources = true;
    } else if (category === 'properties') {
      result.haveProperties = true;
    } else if (category === 'savings') {
      result.haveSavings = true;
    }
  }

  return result;
}

/**
 * Transform asset data record to schema-compatible nested format
 * Asset fields don't have frequency, only amount and notes
 */
export function mapAssetDataToSchema(
  assetData: any,
  assetNotes: Record<string, string>,
  category: 'properties' | 'otherAssets'
): Record<string, any> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return {};

  const result: Record<string, any> = {};
  let hasAnyData = false;
  let hasAnyPositiveAmount = false;
  let hasExplicitToggleFromApi = false;

  // Iterate through schema field mappings
  Object.keys(mapping).forEach(schemaFieldName => {
    const fieldMapping = (mapping as any)[schemaFieldName];
    
    if (typeof fieldMapping !== 'object') {
      return; // Skip non-nested mappings
    }

    // Build nested object from API data
    const nestedObj: any = {};
    let hasData = false;

    if (fieldMapping.amount) {
      const apiValue = (assetData as any)[fieldMapping.amount];
      // Include 0 values so cleared fields show as £0.00 instead of disappearing
      if (apiValue !== undefined && apiValue !== null) {
        nestedObj.amount = String(apiValue);
        hasData = true;
        const numeric =
          typeof apiValue === 'number'
            ? apiValue
            : Number.parseFloat(String(apiValue));
        if (Number.isFinite(numeric) && numeric > 0) {
          hasAnyPositiveAmount = true;
        }
      }
    }

    // Assets don't have frequency fields

    if (fieldMapping.notes) {
      const apiValue = assetNotes[fieldMapping.notes];
      if (apiValue !== undefined && apiValue !== null) {
        nestedObj.notes = apiValue;
        hasData = true;
      }
    }

    // Only add to result if there's actual data
    if (hasData) {
      result[schemaFieldName] = nestedObj;
      hasAnyData = true;
    }
  });

  // Map explicit backend Y/N flags for assets if present (mirrors income API).
  if (category === 'properties') {
    const raw = (assetData as any).haveProperties;
    if (typeof raw === 'string' && raw.trim() !== '') {
      const upper = raw.trim().toUpperCase();
      if (upper === 'Y' || upper === 'N') {
        (result as any).haveProperties = upper === 'Y';
        hasAnyData = true;
        hasExplicitToggleFromApi = true;
      }
    }
  }

  // Auto-set toggle fields based on category when there is a genuine value.
  // Avoid pre-selecting toggles when API has only default 0s.
  if (!hasExplicitToggleFromApi && hasAnyData && hasAnyPositiveAmount) {
    if (category === 'properties') {
      result.haveProperties = true;
    }
  }

  return result;
}

/**
 * Transform schema data to asset data record format
 * Used when saving asset data from form
 */
export function mapSchemaDataToAsset(
  schemaData: Record<string, any>,
  category: 'properties' | 'otherAssets',
  baseRecord: any
): any {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return baseRecord;

  const result: any = { ...baseRecord };

  // Iterate through schema data fields
  Object.keys(schemaData).forEach(fieldName => {
    const fieldValue = schemaData[fieldName];
    
    // Skip toggle buttons and non-data fields
    if (typeof fieldValue === 'boolean' || !fieldValue) {
      return;
    }

    // Check if this field has a mapping
    const fieldMapping = (mapping as any)[fieldName];
    if (!fieldMapping || typeof fieldMapping !== 'object') {
      return;
    }

    // Handle nested structure: { amount, notes }
    if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      // Map/clear amount (treat empty string or empty object as zero)
      if (fieldMapping.amount) {
        if (fieldValue.amount !== undefined) {
          const amt = fieldValue.amount;
          const existing = (result as any)[fieldMapping.amount];
          if (amt === '' || amt === null) {
            if (existing !== undefined && existing !== null) {
              (result as any)[fieldMapping.amount] = 0;
            }
          } else {
            const parsed =
              typeof amt === 'number' ? amt : Number.parseFloat(String(amt));
            if (Number.isFinite(parsed)) {
              (result as any)[fieldMapping.amount] = parsed;
            } else if (existing !== undefined && existing !== null) {
              ;(result as any)[fieldMapping.amount] = 0;
            }
          }
        } else if (Object.keys(fieldValue).length === 0) {
          const existing = (result as any)[fieldMapping.amount];
          if (existing !== undefined && existing !== null) {
            (result as any)[fieldMapping.amount] = 0;
          }
        }
      }
    }
  });

  return result;
}

/**
 * Extract notes fields from asset schema data
 */
export function mapSchemaDataToAssetNotes(
  schemaData: Record<string, any>,
  category: 'properties' | 'otherAssets'
): Record<string, string> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return {};

  const result: Record<string, string> = {};

  Object.keys(schemaData).forEach(fieldName => {
    const fieldValue = schemaData[fieldName];
    
    // Skip non-object values
    if (typeof fieldValue !== 'object' || Array.isArray(fieldValue) || !fieldValue) {
      return;
    }

    const fieldMapping = (mapping as any)[fieldName];
    if (!fieldMapping || typeof fieldMapping !== 'object') {
      return;
    }

    // Map notes
    if (fieldValue.notes !== undefined && fieldMapping.notes) {
      result[fieldMapping.notes] = fieldValue.notes || '';
    }
  });

  return result;
}

/**
 * Transform expense data record to schema-compatible nested format
 */
export function mapExpenseDataToSchema(
  expenseData: ExpenseDataRecord,
  expenseNotes: Record<string, string>,
  category: keyof typeof BUDGET_FIELD_MAPPINGS
): Record<string, any> {
  const mapping = BUDGET_FIELD_MAPPINGS[category];
  if (!mapping) return {};

  const result: Record<string, any> = {};
  let hasAnyData = false;
  let hasAnyPositiveAmount = false;
  let hasExplicitToggleFromApi = false;

  Object.keys(mapping).forEach(schemaFieldName => {
    const fieldMapping = (mapping as any)[schemaFieldName];
    
    if (typeof fieldMapping !== 'object') {
      return;
    }

    const nestedObj: any = {};
    let hasData = false;

    if (fieldMapping.amount) {
      const apiValue = (expenseData as any)[fieldMapping.amount];
      // Include 0 values so cleared fields show as £0.00 instead of disappearing
      if (apiValue !== undefined && apiValue !== null) {
        nestedObj.amount = String(apiValue);
        hasData = true;
        const numeric =
          typeof apiValue === 'number'
            ? apiValue
            : Number.parseFloat(String(apiValue));
        if (Number.isFinite(numeric) && numeric > 0) {
          hasAnyPositiveAmount = true;
        }
      }
    }

    if (fieldMapping.frequency) {
      const apiValue = (expenseData as any)[fieldMapping.frequency];
      if (apiValue !== undefined && apiValue !== null) {
        // Convert API format to UI format
        const uiFrequency = FREQUENCY_API_TO_UI[apiValue] || apiValue;
        nestedObj.frequency = uiFrequency;
        hasData = true;
      } else if (schemaFieldName === 'groundRentServiceCharge') {
        // Default to 'semiannual' for groundRentServiceCharge when not provided by API
        nestedObj.frequency = 'semiannual';
        hasData = true;
      }
    }

    if (fieldMapping.notes) {
      const apiValue = expenseNotes[fieldMapping.notes];
      const overrideNoteKey = (fieldMapping as any).noteFieldName;
      if (
        assignNoteValueToTarget(
          nestedObj,
          overrideNoteKey
            ? [fieldMapping.notes, overrideNoteKey]
            : [fieldMapping.notes],
          apiValue
        )
      ) {
        hasData = true;
      }
    }

    // Map supplier and other additional fields
    const additionalFields = ['landlineSupplier', 'landlineSupplierOther', 'broadbandSupplier',
                              'broadbandSupplierOther', 'mobileSupplier', 'mobileSupplierOther',
                              'gasSupplier', 'gasSupplierOther', 'electricitySupplier', 'electricitySupplierOther',
                              'awarePriorityServices', 'awareCarbonMonoxidePoison'];
    
    additionalFields.forEach(additionalField => {
      if ((fieldMapping as any)[additionalField]) {
        let apiValue = (expenseData as any)[(fieldMapping as any)[additionalField]];
        if (apiValue !== undefined && apiValue !== null) {
          // Convert Y/N back to boolean for specific fields
          if ((additionalField === 'awarePriorityServices' || additionalField === 'awareCarbonMonoxidePoison') && (apiValue === 'Y' || apiValue === 'N')) {
            apiValue = apiValue === 'Y';
          }
          
          nestedObj[additionalField] = apiValue;
          hasData = true;
        }
      }
    });

    if (hasData) {
      // Special placement: gambling group is nested under hobbiesLeisureSports in leisure schema
      if (category === 'leisure' && schemaFieldName === 'gamblingFromHobbiesLeisure') {
        // Ensure hobbiesLeisureSports exists, even if empty
        if (!result['hobbiesLeisureSports']) {
          result['hobbiesLeisureSports'] = {};
        }
        // Merge gambling data into hobbiesLeisureSports
        (result['hobbiesLeisureSports'] as any)['gamblingFromHobbiesLeisure'] = nestedObj;
        hasAnyData = true;
      } else {
        result[schemaFieldName] = nestedObj;
        hasAnyData = true;
      }
    }
  });

  // Map explicit backend Y/N flags to section-level toggles when present.
  const expenseToggleMapping = EXPENSE_TOGGLE_TO_API_FIELD[category as BudgetCategoryKey];
  if (expenseToggleMapping) {
    Object.entries(expenseToggleMapping).forEach(([schemaToggleName, apiFieldName]) => {
      const raw = (expenseData as any)[apiFieldName];
      if (typeof raw === 'string' && raw.trim() !== '') {
        const upper = raw.trim().toUpperCase();
        if (upper === 'Y' || upper === 'N') {
          (result as any)[schemaToggleName] = upper === 'Y';
          hasAnyData = true;
          hasExplicitToggleFromApi = true;
        }
      }
    });
  }

  // Auto-set toggle fields based on category when there is at least one
  // strictly positive amount. This prevents new users (all 0s) from seeing
  // household/utilities/etc. toggles pre-selected.
  if (!hasExplicitToggleFromApi && hasAnyData && hasAnyPositiveAmount) {
    if (category === 'household') {
      result.haveHouseholdExpenses = true;
    } else if (category === 'utilities') {
      result.haveUtilities = true;
    } else if (category === 'care') {
      result.haveCareExpenses = true;
    } else if (category === 'transport') {
      result.haveTransportExpenses = true;
    } else if(category === 'school'){
      result.haveSchoolExpenses = true;
    } else if (category === 'pensionInsurance') {
      result.havePensionInsurance = true;
    } else if (category === 'professionals') {
      result.haveProfessionalCosts = true;
    } else if(category === 'otherSourcesFixed'){
      result.haveOtherFixedExpenses = true;
    } else if (category === 'leisure') {
      result.haveLeisure = true;
    } else if (category === 'housekeeping') {
      result.haveHousekeeping = true;
    } else if (category === 'personalCosts') {
      result.havePersonalCosts = true;
    } else if (category === 'savings') {
      result.haveSavings = true;
    } else if (category === 'debts') {
      result.haveDebts = true;
    }
  }

  // Special-case mapping for debtsAdministration API Y/N → toggle boolean
  if (category === 'debtsAdministration') {
    const apiToggle = (expenseData as any).hasDebtAdminFee;
    if (typeof apiToggle === 'string') {
      result.haveDebtAdministration = apiToggle.toUpperCase() === 'Y';
    }
  }

  return result;
}

/**
 * Format currency values for display
 */
export function formatCurrency(value: number, currency: string = '£'): string {
  return `${currency}${value.toLocaleString()}`;
}

/**
 * Validate frequency mode values
 */
export function isValidFrequencyMode(mode: string): boolean {
  return Object.values(FREQUENCY_MODES).includes(mode as any);
}

/**
 * Get default frequency mode for a category
 */
export function getDefaultFrequencyMode(category: keyof typeof BUDGET_FIELD_MAPPINGS): string | null {
  switch (category) {
    case 'salary':
    case 'benefits':
    case 'pension':
    case 'otherSources':
    case 'household':
    case 'utilities':
    case 'care':
    case 'transport':
    case 'school':
    case 'pensionInsurance':
    case 'professionals':
    case 'otherSourcesFixed':
    case 'leisure':
    case 'housekeeping':
    case 'personalCosts':
    case 'savings':
      return FREQUENCY_MODES.MONTHLY;
    case 'properties':
    case 'otherAssets':
    case 'debts':
      return null;
    default:
      return FREQUENCY_MODES.MONTHLY;
  }
}

// =============================================================================
// PROMPT SYSTEM HELPERS - Field Resolution
// =============================================================================

/**
 * Find schema field name and schema key from API field name
 * Used by the prompt system to locate which schema JSON contains the field
 */
export function findSchemaFieldByApiName(
  apiFieldName: string,
  categoryKey: string
): { schemaFieldName: string; schemaKey: string } {
  
  const mapping = BUDGET_FIELD_MAPPINGS[categoryKey as keyof typeof BUDGET_FIELD_MAPPINGS];
  
  if (!mapping) {
    return { schemaFieldName: '', schemaKey: '' };
  }
  
  // Search through mapping
  for (const [schemaFieldName, fieldMapping] of Object.entries(mapping)) {
    if (typeof fieldMapping === 'object') {
      const fm = fieldMapping as any;
      if (fm.amount === apiFieldName || 
          fm.frequency === apiFieldName || 
          fm.notes === apiFieldName) {
        return {
          schemaFieldName,
          schemaKey: `budget/${getCategoryPath(categoryKey)}`
        };
      }
    }
  }
  
  // Check onboarding fields (not in budget mappings)
  if (apiFieldName === 'customerEmploymentStatus') {
    return {
      schemaFieldName: 'customerEmploymentStatus',
      schemaKey: 'income-onboarding/2-current-employment-status'
    };
  }
  
  if (apiFieldName === 'housingTenure') {
    return {
      schemaFieldName: 'housingTenure',
      schemaKey: 'income-onboarding/1-accomodation-type'
    };
  }
  
  if (apiFieldName === 'debtor1DateOfBirthStr') {
    return {
      schemaFieldName: 'debtor1DateOfBirthStr',
      schemaKey: 'onboarding/1-your-details'
    };
  }
  
  return { schemaFieldName: '', schemaKey: '' };
}

/**
 * Get schema file path from category key
 */
function getCategoryPath(categoryKey: string): string {
  const paths: Record<string, string> = {
    'salary': 'income/1-salary',
    'benefits': 'income/2-benefits',
    'pension': 'income/3-pension',
    'otherSources': 'income/4-other-sources',
    'properties': 'asset/1-properties',
    'otherAssets': 'asset/2-other-assets',
    'household': 'fixedExpense/1-household',
    'utilities': 'fixedExpense/2-utilities',
    'care': 'fixedExpense/3-care',
    'transport': 'fixedExpense/4-transport',
    'school': 'fixedExpense/5-school',
    'pensionInsurance': 'fixedExpense/6-pension-insurance',
    'professionals': 'fixedExpense/7-professionals',
    'otherSourcesFixed': 'fixedExpense/8-other-sources',
    'leisure': 'flexibleExpense/1-leisure',
    'housekeeping': 'flexibleExpense/2-housekeeping',
    'personalCosts': 'flexibleExpense/3-personal-costs',
    'savings': 'flexibleExpense/4-savings',
    'debtsAdministration': 'debts/1-administration',
    'debts': 'debts/2-debts'
  };
  return paths[categoryKey] || '';
}

/**
 * Get human-readable field label from API field name
 * Used to display clickable text in prompts
 */
export function getFieldLabelByApiName(apiFieldName: string): string {
  const labels: Record<string, string> = {
    // Profile fields
    'customerEmploymentStatus': 'Employment status',
    'partnerEmploymentStatus': 'Partner employment status',
    'housingTenure': 'Housing tenure',
    'debtor1DateOfBirthStr': 'Date of birth',
    'dependentChildrenAged16Plus': 'Dependent children aged 16+',
    'dependentChildrenUnder16': 'Dependent children under 16',
    'noOfVehiclesInHouseholds': 'Number of vehicles',
    
    // Income - salary
    'basicIncome': 'Take home salary',
    'debtor2BasicIncome': 'Partner take home salary',
    'otherSalaryWages1': 'Other salary source',
    
    // Income - Benefits
    'otherIncludeUniversalCredit': 'Universal credit',
    'jobSeekerAllowanceContribute': 'Job seeker\'s allowance (contribution)',
    'childBenefit': 'Child benefit',
    'empSupportAllowanceStatutory': 'Employment and Support Allowance (contribution) or Statutory Sick Pay',
    'dlaPipAttendanceAllowance': 'Disability allowance',
    'carerAllowance': 'Carer\'s allowance',
    'housingBenefitLocalAllowance': 'Housing benefit',
    'councilTaxReduction': 'Council tax support',
    'otherBenefits': 'Other benefits',
    'workingTaxCredit': 'Working tax credit',
    'childTaxCredit': 'Child tax credit',
    
    // Income - Pension
    'statePension': 'State pension',
    'privatePension': 'Private pension',
    'pensionCredit': 'Pension credit',
    'pensionOthers': 'Other pension',
    
    // Income - Other sources
    'maintenanceOrChildSupport': 'Child maintenance or support',
    'bordersAndLodger': 'Boarders and lodgers',
    'nonDependantContributions': 'Non-dependant contributions',
    'studentLoansAndGrants': 'Student loans and grants',
    'otherIncome1': 'Money from other sources',
    
    // Assets
    'totalValueProperties': 'Total value of properties',
    'mortgageOutstanding': 'Mortgage outstanding',
    'securedLoanOutstanding': 'Secured loan outstanding',
    'valueOfVehicles': 'Value of vehicles',
    'assetSavings': 'Savings',
    'otherAssets': 'Other assets',
    
    // Fixed expenses - Household
    'houseRent': 'Rent',
    'mortgage': 'Mortgage',
    'secondMortgage': 'Second mortgage',
    'otherSecuredLoans': 'Secured loans',
    'councilTax': 'Council tax',
    'groundRentAndServiceCharges': 'Ground rent/ service charge',
    'tvLicence': 'TV licence',
    'otherRentalAppliances': 'Appliance/ furniture rental',
    'buildingContentsInsurance': 'Building/ contents insurance',
    'otherHomeCosts': 'Other household expenses',
    
    // Add more as needed...
  };
  
  return labels[apiFieldName] || apiFieldName.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
}

/**
 * Get API field name from schema field name
 * Used when we have the schema field name and need to find the API field
 */
export function getApiFieldNameFromSchemaField(
  schemaFieldName: string,
  categoryKey: keyof typeof BUDGET_FIELD_MAPPINGS
): string | null {
  const mapping = BUDGET_FIELD_MAPPINGS[categoryKey];
  if (!mapping) return null;
  
  const fieldMapping = (mapping as any)[schemaFieldName];
  if (fieldMapping && typeof fieldMapping === 'object') {
    // Return the amount field as the primary API field
    return fieldMapping.amount || fieldMapping.notes || null;
  }
  
  return null;
}
